export interface Property {
  id: string;
  slug: string; // URL-friendly identifier
  name: string;
  type: "Condo" | "Townhome";
  beds: number;
  baths: number;
  sqft: string;
  priceRange?: string;
  description: string;
  features: string[];
  floorPlanImage: string;
  matterportUrl?: string;
  hasVirtualTour: boolean;
  images: string[];
  // Video hero support (optional)
  heroVideoMp4?: string; // MP4 video URL for hero background
  heroVideoWebm?: string; // WebM video URL for hero background (optional, better compression)
  heroPosterImage?: string; // Static image shown while video loads
  viewDirection: "Lagoon" | "Courtyard" | "Garden" | "Mixed";
  finishesLevel: "Standard" | "Premium" | "Luxury";
  appliances: string[];
  parkingSpaces: number;
  storage: string[];
  petFriendly: boolean;
  availabilityDate: string;
  floorLevels: string[];
  highlights: string[];
  featuredAmenities?: string[]; // Array of amenity IDs that are particularly relevant to this property
}

export const properties: Property[] = [
  {
    id: "1bed-luxury",
    slug: "1-bedroom-luxury-condo",
    name: "1-Bedroom",
    type: "Condo",
    beds: 1,
    baths: 1,
    sqft: "650-750",
    priceRange: "$350,000 - $425,000",
    description:
      "Perfect for professionals or couples seeking a low-maintenance luxury lifestyle with stunning lagoon views.",
    features: [
      "Open-concept living space",
      "Premium kitchen finishes",
      "Spacious master bedroom",
      "Luxury bathroom with walk-in shower",
      "Private balcony",
      "In-unit laundry",
    ],
    floorPlanImage: "/img/properties/floor-plan-sample.png",
    matterportUrl:
      "https://my.matterport.com/show?play=1&lang=en-US&m=7BPHNCLZCGW",
    hasVirtualTour: true,
    images: [
      "/img/properties/1bed-living.png",
      "/img/properties/1bed-kitchen.png",
      "/img/properties/1bed-dining.png",
      "/img/properties/1bed-bedroom.png",
      "/img/properties/1bed-bathroom.png",
      "/img/properties/1bed-terrace.png",
    ],
    viewDirection: "Lagoon",
    finishesLevel: "Premium",
    appliances: [
      "Stainless steel appliances",
      "Quartz countertops",
      "Custom cabinetry",
      "Energy-efficient washer/dryer",
    ],
    parkingSpaces: 1,
    storage: ["Walk-in closet", "Additional storage unit"],
    petFriendly: true,
    availabilityDate: "2028",
    floorLevels: ["2nd-4th floors"],
    highlights: [
      "Lagoon-facing balcony",
      "Premium finishes throughout",
      "Smart home ready",
      "Concierge services",
    ],
    featuredAmenities: [
      "lagoon-views",
      "wellness-gym",
      "community-hub",
      "modern-architecture",
    ],
  },
  {
    id: "2bed-luxury",
    slug: "2-bedroom-luxury-condo",
    name: "2-Bedroom",
    type: "Condo",
    beds: 2,
    baths: 2,
    sqft: "950-1,150",
    priceRange: "$450,000 - $575,000",
    description:
      "Spacious living for small families or those who work from home, featuring dual master suites.",
    features: [
      "Two master bedrooms",
      "Open-concept kitchen and living",
      "Dual bathrooms with premium finishes",
      "Private balcony or terrace",
      "In-unit laundry",
      "Flexible den space",
    ],
    floorPlanImage: "/img/properties/floor-plan-sample.png",
    matterportUrl:
      "https://my.matterport.com/show?play=1&lang=en-US&m=7BPHNCLZCGW",
    hasVirtualTour: true,
    images: [
      "/img/properties/2bed-living.png",
      "/img/properties/2bed-kitchen.png",
      "/img/properties/2bed-dining.png",
      "/img/properties/2bed-guest.png",
      "/img/properties/2bed-master-bedroom.png",
      "/img/properties/2bed-master-bathroom.png",
      "/img/properties/2bed-guest-bathroom.png",
      "/img/properties/2bed-terrace.png",
      "/img/properties/2bed-pool.png",
    ],
    viewDirection: "Mixed",
    finishesLevel: "Luxury",
    appliances: [
      "High-end stainless steel appliances",
      "Quartz countertops",
      "Custom cabinetry",
      "Smart home integration",
      "Energy-efficient washer/dryer",
    ],
    parkingSpaces: 1,
    storage: ["Master walk-in closet", "Guest closet", "Additional storage"],
    petFriendly: true,
    availabilityDate: "2028",
    floorLevels: ["3rd-6th floors"],
    highlights: [
      "Dual master suites",
      "Flexible den/office space",
      "Premium lagoon views",
      "Smart home technology",
    ],
    featuredAmenities: [
      "lagoon-views",
      "wellness-gym",
      "community-hub",
      "modern-architecture",
    ],
  },
  {
    id: "3bed-townhome",
    slug: "3-bedroom-luxury-townhome",
    name: "3-Bedroom",
    type: "Townhome",
    beds: 3,
    baths: 2.5,
    sqft: "1,400-1,600",
    priceRange: "$650,000 - $850,000",
    description:
      "Multi-level townhomes with private outdoor spaces, premium finishes, and family-friendly layouts.",
    features: [
      "Three bedrooms across two levels",
      "Open-concept main floor",
      "Private rooftop terrace",
      "Two-car garage",
      "Powder room on main level",
      "Flexible basement space",
    ],
    floorPlanImage: "/img/properties/floor-plan-sample.png",
    matterportUrl:
      "https://my.matterport.com/show?play=1&lang=en-US&m=7BPHNCLZCGW",
    hasVirtualTour: true,
    images: [
      "/img/properties/3bed-living.png",
      "/img/properties/3bed-kitchen.png",
      "/img/properties/3bed-dining.png",
      "/img/properties/3bed-guest1.png",
      "/img/properties/3bed-guest2.png",
      "/img/properties/3bed-master-bedroom.png",
      "/img/properties/3bed-master-bathroom.png",
      "/img/properties/3bed-guest-bathroom.png",
      "/img/properties/3bed-terrace.png",
      "/img/properties/3bed-pool.png",
    ],
    viewDirection: "Garden",
    finishesLevel: "Luxury",
    appliances: [
      "Professional-grade appliances",
      "Quartz countertops",
      "Custom cabinetry",
      "Smart home integration",
      "High-efficiency washer/dryer",
    ],
    parkingSpaces: 2,
    storage: [
      "Master walk-in closet",
      "Additional bedrooms",
      "Basement storage",
      "Garage storage",
    ],
    petFriendly: true,
    availabilityDate: "2028",
    floorLevels: ["Ground level"],
    highlights: [
      "Private rooftop terrace",
      "Two-car garage",
      "Multi-level living",
      "Family-friendly design",
      "Premium outdoor space",
    ],
    featuredAmenities: [
      "gardens",
      "boat-dock",
      "community-hub",
      "modern-architecture",
    ],
  },
];

export const getPropertyById = (id: string): Property | undefined => {
  return properties.find(
    (property) => property.id === id || property.slug === id
  );
};

export const getPropertiesByType = (type: "Condo" | "Townhome"): Property[] => {
  return properties.filter((property) => property.type === type);
};

export const getPropertiesWithVirtualTour = (): Property[] => {
  return properties.filter((property) => property.hasVirtualTour);
};

export const getNextProperty = (currentId: string): Property | null => {
  const currentIndex = properties.findIndex(
    (property) => property.id === currentId || property.slug === currentId
  );
  if (currentIndex === -1) return null;

  const nextIndex = (currentIndex + 1) % properties.length;
  return properties[nextIndex];
};

export const getPropertiesByBedrooms = (beds: number): Property[] => {
  return properties.filter((property) => property.beds === beds);
};
